import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import {
  BoutonPrincipal,
  Carte,
  Chargement,
  Compteur,
  EnteteEcran,
  MessageErreur,
  Pagination,
  Pastille,
} from '../components/ui/Primitives';
import { BarreSites } from '../components/ui/BarreSites';
import { BandeauExpedition } from '../components/notifications/BandeauExpedition';
import { ApercuMailEntreprise } from '../components/absences/ApercuMailEntreprise';
import { CarteJustificatifs } from '../components/absences/CarteJustificatifs';
import { ModaleDetailAbsence } from '../components/absences/ModaleDetailAbsence';
import { useToast } from '../components/ui/useToast';
import { usePerimetre } from '../site/usePerimetre';
import { useAuth } from '../auth/useAuth';
import { useTaillePage } from '../lib/useTaillePage';
import { ErreurApi } from '../lib/api';
import {
  useAbsences,
  useGenererAvertissementsDus,
  type LigneJournee,
  type PeriodeAbsences,
} from '../lib/useAbsences';
import { CHEMIN_ABSENTEISME } from '../lib/useAbsenteisme';

const PERIODES: { valeur: PeriodeAbsences; libelle: string }[] = [
  { valeur: 'MOIS', libelle: '30 derniers jours' },
  { valeur: 'TRIMESTRE', libelle: '3 derniers mois' },
  { valeur: 'TOUT', libelle: 'Tout le parcours' },
];

type Filtre = 'TOUTES' | 'A_TRAITER' | 'SEUIL';

const COLONNES = ['Apprenant', 'Date', 'Type', 'Justifiée', 'Cumul / seuil', 'Entreprise notifiée', ''];

function taux(valeur: number | null | undefined): string {
  return valeur === null || valeur === undefined ? '—' : `${String(valeur).replace('.', ',')} %`;
}

/** Une ligne demande un geste : mail à envoyer, justificatif à examiner, courrier dû. */
function aTraiter(ligne: LigneJournee): boolean {
  return (
    ligne.information.etat === 'A_INFORMER' ||
    ligne.information.etat === 'SANS_ADRESSE' ||
    ligne.justification.etat === 'EN_EXAMEN' ||
    ligne.avertissement.palierDu !== null
  );
}

function couleurCumul(ligne: LigneJournee): string {
  if (ligne.cumul.seuilDepasse) return 'text-red-600';
  return ligne.cumul.margeAvantSeuil <= 1 ? 'text-amber-600' : 'text-codialis-teal';
}

/**
 * Écran « Absences & avertissements » du pôle pédagogique (CDC §6.6).
 *
 * L'émargement est la source unique : une absence n'est définitive qu'à la
 * clôture de sa feuille, qui informe l'employeur — matin, après-midi ou
 * journée complète — et transmet le courrier d'avertissement au palier
 * atteint. L'écran montre ce qui est parti, ce qui reste à faire à la main,
 * et les justificatifs déposés par les apprenants. Rien n'y est recalculé.
 */
export function PedAbsencesPage(): JSX.Element {
  const { site } = usePerimetre();
  const { peutAcceder } = useAuth();
  const toast = useToast();
  const [periode, setPeriode] = useState<PeriodeAbsences>('MOIS');
  const [filtre, setFiltre] = useState<Filtre>('TOUTES');
  const [recherche, setRecherche] = useState('');
  const [choisie, setChoisie] = useState<string | null>(null);
  const [detail, setDetail] = useState<string | null>(null);
  const { data, isLoading, error } = useAbsences(site, periode);
  const generer = useGenererAvertissementsDus();
  const [page, setPage] = useState(1);
  const [taillePage, setTaillePage] = useTaillePage();

  const compteurs = data?.compteurs;
  const lignes = useMemo(() => {
    const terme = recherche.trim().toLowerCase();
    return (data?.journees ?? []).filter((ligne) => {
      if (filtre === 'A_TRAITER' && !aTraiter(ligne)) return false;
      if (filtre === 'SEUIL' && !ligne.cumul.seuilDepasse) return false;
      return !terme || ligne.apprenant.toLowerCase().includes(terme);
    });
  }, [data?.journees, filtre, recherche]);

  useEffect(() => {
    setPage(1);
  }, [site, periode, filtre, recherche, taillePage]);

  const pages = Math.max(1, Math.ceil(lignes.length / taillePage));
  const affichees = lignes.slice((page - 1) * taillePage, page * taillePage);
  const toutes = data?.journees ?? [];
  // Par défaut, la plus récente qui a un mail à montrer.
  const ligneChoisie =
    toutes.find((ligne) => ligne.cle === choisie) ??
    toutes.find((ligne) => ligne.demiJournees.some((demi) => demi.apercu)) ??
    toutes[0] ??
    null;
  const ligneDetail = toutes.find((ligne) => ligne.cle === detail) ?? null;
  const duJour = compteurs?.duJour;
  const courriersDus = compteurs?.courriersDus ?? 0;

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Absences & avertissements"
        sousTitre="Pôle pédagogique · émargement obligatoire, mail entreprise, courrier au-delà du seuil"
        actions={
          <>
            <Link
              to="/emargement"
              className="inline-flex min-h-[44px] items-center rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50"
            >
              Feuille d’émargement
            </Link>
            <Link
              to={CHEMIN_ABSENTEISME}
              className="inline-flex min-h-[44px] items-center rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50"
            >
              Taux d’absentéisme
            </Link>
            <BoutonPrincipal
              disabled={courriersDus === 0 || generer.isPending}
              onClick={() => generer.mutate(site, { onSuccess: (reponse) => toast(reponse.message) })}
            >
              Générer les courriers ({courriersDus})
            </BoutonPrincipal>
          </>
        }
      />

      <BarreSites />

      <BandeauExpedition
        etat={data?.expedition}
        contexte="Les mails aux entreprises et les courriers d’avertissement restent tracés tant que l’envoi n’est pas raccordé."
      />

      {data && (
        <div className="rounded-2xl bg-codialis-sand p-4 text-sm text-codialis-navy">
          {data.automatismes.information && data.automatismes.avertissement
            ? 'À la clôture de chaque demi-journée, l’employeur est informé par mail et le courrier d’avertissement part au palier atteint : au seuil de la formation, puis à chaque multiple du seuil.'
            : `Automatismes coupés dans le module d’alertes sur cette antenne : ${[
                !data.automatismes.information ? 'mail à l’entreprise' : null,
                !data.automatismes.avertissement ? 'courrier d’avertissement' : null,
              ]
                .filter(Boolean)
                .join(' et ')}. Ces gestes se font d’ici, à la main.`}
          {data.seancesOuvertes > 0 && (
            <>
              {' '}
              {data.seancesOuvertes} feuille(s) du jour ou passée(s) ne sont pas encore clôturées : leurs
              absences restent provisoires et rien n’est parti.
            </>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Compteur
          libelle="Absences du jour"
          valeur={compteurs?.absencesDuJour ?? '—'}
          detail={
            duJour
              ? `${duJour.matin} matin · ${duJour.apresMidi} après-midi · ${duJour.journee} journée`
              : undefined
          }
          ton="warm"
        />
        <Compteur
          libelle="Mails entreprise envoyés"
          valeur={compteurs?.mailsEnvoyesDuJour ?? '—'}
          detail={
            compteurs && compteurs.aInformer > 0
              ? `aujourd’hui · ${compteurs.aInformer} demi-journée(s) à informer`
              : 'aujourd’hui · automatique, type précisé'
          }
          ton="blue"
        />
        <Compteur
          libelle="Seuil atteint"
          valeur={compteurs?.seuilAtteint ?? '—'}
          detail={courriersDus > 0 ? `${courriersDus} courrier(s) à produire` : 'courrier d’avertissement transmis'}
          ton="red"
        />
        <Compteur
          libelle="Taux de présence"
          valeur={taux(compteurs?.tauxPresence)}
          detail="objectif > 90 % · feuilles clôturées de la période"
          ton="mint"
        />
      </div>

      {generer.error instanceof ErreurApi && <MessageErreur message={generer.error.message} />}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="min-w-0 space-y-6 lg:col-span-2">
          <CarteJustificatifs justificatifs={data?.justificatifs ?? []} />

          <Carte
            titre="Absences en cours"
            actions={
              <select
                aria-label="Période"
                value={periode}
                onChange={(evenement) => setPeriode(evenement.target.value as PeriodeAbsences)}
                className="min-h-[40px] rounded-lg border border-gray-200 bg-gray-50 px-2 text-xs font-semibold text-codialis-navy"
              >
                {PERIODES.map((entree) => (
                  <option key={entree.valeur} value={entree.valeur}>
                    {entree.libelle}
                  </option>
                ))}
              </select>
            }
            sansBordure
          >
            <div className="flex flex-col gap-3 border-b border-gray-100 px-4 py-3 sm:flex-row sm:items-center sm:px-6">
              <div className="relative min-w-0 flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-codialis-gray" aria-hidden="true" />
                <input
                  type="search"
                  aria-label="Rechercher un apprenant"
                  value={recherche}
                  onChange={(evenement) => setRecherche(evenement.target.value)}
                  placeholder="Rechercher un apprenant"
                  className="min-h-[40px] w-full rounded-lg border border-gray-200 bg-gray-50 pl-9 pr-3 text-sm"
                />
              </div>
              <div className="flex flex-wrap gap-2" role="group" aria-label="Filtrer les absences">
                {(
                  [
                    ['TOUTES', 'Toutes'],
                    ['A_TRAITER', 'À traiter'],
                    ['SEUIL', 'Seuil atteint'],
                  ] as [Filtre, string][]
                ).map(([valeur, libelle]) => (
                  <button
                    key={valeur}
                    type="button"
                    aria-pressed={filtre === valeur}
                    onClick={() => setFiltre(valeur)}
                    className={`min-h-[40px] rounded-lg border px-3 text-xs font-bold transition ${
                      filtre === valeur
                        ? 'border-codialis-mint bg-codialis-sand text-codialis-navy'
                        : 'border-gray-200 bg-gray-50 text-codialis-gray'
                    }`}
                  >
                    {libelle}
                  </button>
                ))}
              </div>
            </div>

            {error && (
              <div className="p-4 sm:p-6">
                <MessageErreur
                  message={error instanceof ErreurApi ? error.message : 'Les absences n’ont pas pu être chargées.'}
                />
              </div>
            )}
            {isLoading && <Chargement libelle="Chargement des absences…" />}

            {!isLoading && !error && lignes.length === 0 && (
              <p className="px-4 py-10 text-center text-sm text-codialis-gray sm:px-6">
                {toutes.length === 0
                  ? 'Aucune absence constatée sur ce périmètre et cette période.'
                  : 'Aucune absence ne correspond à ce filtre.'}
              </p>
            )}

            {lignes.length > 0 && (
              <>
                {/* Sous l'écran large, une carte par journée : le tableau à sept
                    colonnes défilait de côté sur la tablette. */}
                <ul className="grid grid-cols-1 gap-3 p-4 md:grid-cols-2 xl:hidden">
                  {affichees.map((ligne) => (
                    <li
                      key={ligne.cle}
                      className={`min-w-0 space-y-2 rounded-xl border p-4 ${
                        ligneChoisie?.cle === ligne.cle ? 'border-codialis-mint bg-codialis-mint/5' : 'border-gray-100'
                      }`}
                    >
                      <div className="flex min-w-0 items-start justify-between gap-2">
                        <button
                          type="button"
                          onClick={() => setChoisie(ligne.cle)}
                          className="min-w-0 text-left font-semibold text-codialis-navy hover:text-codialis-sky [@media(pointer:coarse)]:min-h-[40px]"
                        >
                          {ligne.apprenant}
                        </button>
                        <Pastille texte={ligne.typeLibelle} ton={ligne.typeTon} />
                      </div>
                      <div className="text-[11px] text-codialis-gray">
                        {ligne.jourLisible} · {ligne.siteCode}
                        {ligne.mineur ? ' · mineur' : ''}
                        {ligne.provisoire ? ' · feuille ouverte' : ''}
                      </div>
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        <span className="text-codialis-gray">Justifiée : {ligne.justification.libelle}</span>
                        <span className={`font-mono font-bold ${couleurCumul(ligne)}`}>
                          {ligne.cumul.injustifiees} / {ligne.cumul.seuil}
                        </span>
                        <Pastille texte={ligne.information.libelle} ton={ligne.information.ton} />
                      </div>
                      <div className="flex flex-wrap gap-3">
                        <button
                          type="button"
                          onClick={() => setDetail(ligne.cle)}
                          className={`min-h-[40px] text-xs font-semibold hover:underline ${
                            ligne.avertissement.palierDu !== null ? 'text-red-600' : 'text-codialis-sky'
                          }`}
                        >
                          {ligne.avertissement.palierDu !== null ? 'Avertissement' : 'Détail'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setChoisie(ligne.cle)}
                          className="min-h-[40px] text-xs font-semibold text-codialis-gray hover:underline"
                        >
                          Voir le mail
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>

                <div className="relative hidden overflow-x-auto xl:block">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-codialis-gray">
                      <tr>
                        {COLONNES.map((colonne, index) => (
                          <th key={colonne || index} className="whitespace-nowrap px-3 py-3 text-left font-bold">
                            {colonne || <span className="sr-only">Actions</span>}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {affichees.map((ligne) => (
                        <tr
                          key={ligne.cle}
                          onClick={() => setChoisie(ligne.cle)}
                          className={`cursor-pointer transition hover:bg-gray-50 ${
                            ligneChoisie?.cle === ligne.cle ? 'bg-codialis-mint/5' : ''
                          }`}
                        >
                          <td className="min-w-[140px] px-3 py-3">
                            <div className="font-semibold text-codialis-navy">{ligne.apprenant}</div>
                            <div className="text-[11px] text-codialis-gray">
                              {ligne.siteCode}
                              {ligne.mineur ? ' · mineur' : ''}
                            </div>
                          </td>
                          <td className="px-3 py-3 font-mono text-xs text-codialis-gray">{ligne.jourLisible}</td>
                          <td className="px-3 py-3">
                            <Pastille texte={ligne.typeLibelle} ton={ligne.typeTon} />
                          </td>
                          <td className="px-3 py-3 text-xs text-codialis-gray">{ligne.justification.libelle}</td>
                          <td className="px-3 py-3">
                            <span className={`font-mono text-xs font-bold ${couleurCumul(ligne)}`}>
                              {ligne.cumul.injustifiees} / {ligne.cumul.seuil}
                            </span>
                          </td>
                          <td className="px-3 py-3">
                            <Pastille texte={ligne.information.libelle} ton={ligne.information.ton} />
                          </td>
                          <td className="px-3 py-3 text-right">
                            <button
                              type="button"
                              onClick={(evenement) => {
                                evenement.stopPropagation();
                                setDetail(ligne.cle);
                              }}
                              className={`whitespace-nowrap text-xs font-semibold hover:underline ${
                                ligne.avertissement.palierDu !== null ? 'text-red-600' : 'text-codialis-sky'
                              }`}
                            >
                              {ligne.avertissement.palierDu !== null ? 'Avertissement' : 'Détail'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <Pagination
                  page={page}
                  pages={pages}
                  total={lignes.length}
                  affiches={affichees.length}
                  taillePage={taillePage}
                  onPage={setPage}
                  onChangerTaille={setTaillePage}
                  uniteLabel="absence"
                />
              </>
            )}
          </Carte>

          <Carte titre="Émargement · présentiel & e-learning" sansBordure>
            <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 sm:p-6">
              <div className="rounded-xl bg-gray-50 p-4">
                <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-codialis-gray">Présentiel</div>
                <div className="mb-1 text-sm font-semibold text-codialis-navy">Feuille d’émargement dédiée</div>
                <p className="text-xs text-codialis-gray">
                  Signature de l’apprenant et du formateur par demi-journée, sur tablette. La clôture
                  archive le PV, porte absents les non-émargés et informe l’employeur.
                </p>
                <Link to="/emargement" className="mt-3 inline-flex items-center [@media(pointer:coarse)]:min-h-[40px] text-xs font-bold text-codialis-sky hover:underline">
                  Ouvrir la feuille du jour →
                </Link>
              </div>
              <div className="rounded-xl bg-gray-50 p-4">
                <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-codialis-gray">E-learning</div>
                <div className="mb-1 text-sm font-semibold text-codialis-navy">Émargement numérique + caméra obligatoire</div>
                <p className="text-xs text-codialis-gray">
                  L’apprenant émarge dans la salle, caméra allumée, vérifiée par la plateforme. Une coupure
                  alerte le formateur, qui le relance ; ses absences suivent la même règle, le même mail et
                  le même seuil.
                </p>
                <Link to="/form-elearning" className="mt-3 inline-flex items-center [@media(pointer:coarse)]:min-h-[40px] text-xs font-bold text-codialis-sky hover:underline">
                  Voir la salle e-learning →
                </Link>
              </div>
            </div>
          </Carte>
        </div>

        <div className="min-w-0 space-y-6">
          <ApercuMailEntreprise ligne={ligneChoisie} automatique={data?.automatismes.information ?? true} />

          <Carte titre="Seuils par formation" sansBordure>
            <ul className="divide-y divide-gray-100">
              {(data?.seuils ?? []).map((seuil) => (
                <li key={seuil.formationId} className="flex items-center justify-between gap-3 px-4 py-3 sm:px-6">
                  <span className="min-w-0 text-sm text-codialis-navy">
                    {seuil.titre} <span className="text-codialis-gray">({seuil.code})</span>
                  </span>
                  <span className="shrink-0 text-right text-xs text-codialis-gray">
                    {seuil.seuil} demi-journée{seuil.seuil > 1 ? 's' : ''}
                  </span>
                </li>
              ))}
            </ul>
            <div className="space-y-2 px-4 py-3 text-[11px] text-codialis-gray sm:px-6">
              <p>
                Au seuil de la formation, puis à chaque multiple du seuil, un courrier d’avertissement
                est généré et transmis à l’apprenant, à son représentant légal s’il est mineur et à
                l’employeur, copie au pôle pédagogique. Seules les absences non justifiées comptent.
              </p>
              <p>
                Le seuil se règle formation par formation : à défaut,{' '}
                {data?.seuilParDefaut ?? 5} demi-journées.
              </p>
              {peutAcceder('adm-formations') && (
                <Link to="/adm-formations" className="inline-flex items-center [@media(pointer:coarse)]:min-h-[40px] font-bold text-codialis-sky hover:underline">
                  Régler les seuils →
                </Link>
              )}
            </div>
          </Carte>
        </div>
      </div>

      <ModaleDetailAbsence
        ligne={ligneDetail}
        motifs={data?.motifs ?? []}
        ouvrirDossier={peutAcceder('adm-dossier')}
        onFermer={() => setDetail(null)}
      />
    </div>
  );
}
